// pages/sections/TableSection.js
// Renders a section of type 'table'.
// Each row is a PE record; editable cells are determined by field.allowedRoles.
// Sign-off rows have a toggle slider per row.

import { useState } from 'react';
import './TableSection.css';

export default function TableSection({ section, rows, activeRole, readOnly, onSave }) {
  const { fields = [], roles = [] } = section;
  const [pending, setPending]   = useState({});  // { `${recordKey}|${fieldKey}`: value }
  const [saving,  setSaving]    = useState(false);

  const editableFields = fields.filter(f =>
    !f.isPeField && (f.allowedRoles === null || f.allowedRoles?.includes(activeRole))
  );

  // Determine if this role's action is sign-off only
  const roleConfig   = roles.find(r => r.role === activeRole);
  const isSignoffRole = roleConfig?.actionType === 'signoff';

  function cellKey(recordKey, fieldKey) { return `${recordKey}|${fieldKey}`; }

  function handleCellChange(recordKey, fieldKey, value) {
    setPending(prev => ({ ...prev, [cellKey(recordKey, fieldKey)]: value }));
  }

  function handleSignoff(recordKey, currentlySignedOff) {
    // Optimistic: toggle immediately then save
    const newVal = !currentlySignedOff;
    handleCellChange(recordKey, '_signoff', newVal);
    onSave([{ recordKey, fieldKey: '_signoff', value: null, isSignoff: true }])
      .catch(err => {
        console.error('signoff save error', err);
        setPending(prev => { const n = { ...prev }; delete n[cellKey(recordKey, '_signoff')]; return n; });
      });
  }

  async function handleSaveRow(recordKey) {
    const entries = [];
    for (const f of editableFields) {
      const key = cellKey(recordKey, f.fieldKey);
      if (key in pending) {
        entries.push({ recordKey, fieldKey: f.fieldKey, value: pending[key], isSignoff: false });
      }
    }
    if (!entries.length) return;
    setSaving(true);
    try {
      await onSave(entries);
      setPending(prev => {
        const n = { ...prev };
        entries.forEach(e => delete n[cellKey(e.recordKey, e.fieldKey)]);
        return n;
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveAll() {
    const entries = [];
    for (const [key, value] of Object.entries(pending)) {
      const [recordKey, fieldKey] = key.split('|');
      if (fieldKey !== '_signoff') {
        entries.push({ recordKey, fieldKey, value, isSignoff: false });
      }
    }
    if (!entries.length) return;
    setSaving(true);
    try {
      await onSave(entries);
      setPending({});
    } finally {
      setSaving(false);
    }
  }

  const hasPending = Object.keys(pending).some(k => !k.endsWith('|_signoff'));

  return (
    <div className="table-section">
      {hasPending && !readOnly && (
        <div className="table-section-toolbar">
          <span className="unsaved-hint">{Object.keys(pending).length} unsaved change(s)</span>
          <button className="save-all-btn" onClick={handleSaveAll} disabled={saving}>
            {saving ? 'Saving...' : 'Save All Changes'}
          </button>
        </div>
      )}

      <div className="table-wrapper">
        <table className="di-table">
          <thead>
            <tr>
              {fields.map(f => (
                <th key={f.fieldKey} className={f.isPeField ? 'col-pe' : 'col-input'}>
                  {f.label}
                  {f.isPeField && <span className="pe-badge">PE</span>}
                </th>
              ))}
              {/* Sign-off column for roles that sign off */}
              <th className="col-signoff">Sign-off</th>
              {/* Save button per row if not signoff-only role */}
              {!isSignoffRole && !readOnly && <th className="col-actions" />}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const signoffStatus = row.signoffs?.[activeRole];
              const signedOff     = signoffStatus?.signedOff || pending[cellKey(row.recordKey, '_signoff')] === true;
              const rowHasPending = Object.keys(pending).some(k => k.startsWith(row.recordKey + '|') && !k.endsWith('|_signoff'));

              return (
                <tr key={row.recordKey} className={signedOff ? 'row-signedoff' : ''}>
                  {fields.map(f => {
                    const currentValue = pending[cellKey(row.recordKey, f.fieldKey)]
                      ?? row.fields?.[f.fieldKey]?.value
                      ?? '';
                    const isEditable = !readOnly && !f.isPeField && (f.allowedRoles === null || f.allowedRoles?.includes(activeRole));
                    const auditedBy  = row.fields?.[f.fieldKey]?.auditedBy;

                    return (
                      <td key={f.fieldKey} className={f.isPeField ? 'cell-pe' : 'cell-input'}>
                        {isEditable ? (
                          f.fieldType === 'boolean' ? (
                            <input
                              type="checkbox"
                              checked={!!currentValue}
                              onChange={e => handleCellChange(row.recordKey, f.fieldKey, e.target.checked)}
                            />
                          ) : f.fieldType === 'date' ? (
                            <input
                              type="date"
                              className="cell-input-ctrl"
                              value={currentValue || ''}
                              onChange={e => handleCellChange(row.recordKey, f.fieldKey, e.target.value)}
                            />
                          ) : (
                            <input
                              type={f.fieldType === 'number' ? 'number' : 'text'}
                              className="cell-input-ctrl"
                              value={currentValue || ''}
                              onChange={e => handleCellChange(row.recordKey, f.fieldKey, e.target.value)}
                            />
                          )
                        ) : (
                          <span className="cell-display" title={auditedBy ? `By ${auditedBy.userId} (${auditedBy.role})` : undefined}>
                            {String(currentValue ?? '')}
                            {auditedBy && <i className="fas fa-pencil-alt cell-audit-icon" />}
                          </span>
                        )}
                      </td>
                    );
                  })}

                  {/* Sign-off toggle */}
                  <td className="cell-signoff">
                    {readOnly ? (
                      <span className={`signoff-badge ${signedOff ? 'done' : 'pending'}`}>
                        {signedOff ? <i className="fas fa-check" /> : <i className="fas fa-minus" />}
                      </span>
                    ) : (
                      <label className="toggle-switch" title={signedOff ? 'Remove sign-off' : 'Sign off this row'}>
                        <input
                          type="checkbox"
                          checked={signedOff}
                          onChange={() => handleSignoff(row.recordKey, signedOff)}
                        />
                        <span className="toggle-slider" />
                      </label>
                    )}
                  </td>

                  {/* Per-row save (non-signoff roles) */}
                  {!isSignoffRole && !readOnly && (
                    <td className="cell-actions">
                      {rowHasPending && (
                        <button
                          className="save-row-btn"
                          onClick={() => handleSaveRow(row.recordKey)}
                          disabled={saving}
                        >
                          Save
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {rows.length === 0 && (
        <div className="table-empty">No records found for this section and period.</div>
      )}
    </div>
  );
}
